/**
 * Marquee (rubber-band) selection + reliable shift/ctrl/meta-click multi-select
 * for the GrapesJS canvas iframe.
 *
 * Usage:
 *   const tearDown = setupMarqueeSelection(editor);
 *   // later, on unmount:
 *   tearDown();
 *
 * Behavior:
 *   - Drag from empty space on the canvas to draw a selection rectangle.
 *     Releases call editor.select(...) with every top-level component whose
 *     bounding rect intersects the marquee. Holding shift/ctrl/meta during
 *     the drag adds to the current selection instead of replacing it.
 *   - Shift/ctrl/meta + click on a component toggles it in the selection
 *     (works regardless of GrapesJS internal multipleSelection behavior).
 *   - Escape cancels an in-progress marquee.
 */

const MARQUEE_STYLE_ID = 'lpc-marquee-style';
const MARQUEE_CLASS = 'lpc-marquee-rect';
const ACTIVE_BODY_CLASS = 'lpc-marquee-active';
const MIN_DRAG_PX = 3;

function injectMarqueeStyles(doc) {
  if (doc.getElementById(MARQUEE_STYLE_ID)) return;
  const style = doc.createElement('style');
  style.id = MARQUEE_STYLE_ID;
  style.textContent = `
    .${MARQUEE_CLASS} {
      position: absolute;
      border: 1px dashed #3b97e3;
      background: rgba(59, 151, 227, 0.12);
      pointer-events: none;
      z-index: 2147483647;
      box-sizing: border-box;
    }
    body.${ACTIVE_BODY_CLASS},
    body.${ACTIVE_BODY_CLASS} * {
      user-select: none !important;
      -webkit-user-select: none !important;
    }
  `;
  (doc.head || doc.documentElement).appendChild(style);
}

/** True for elements that count as empty canvas (not a real component target). */
function isEmptyCanvasTarget(target, body) {
  if (!target) return true;
  if (target === body) return true;
  if (target.nodeType !== 1) return true;
  if (target === target.ownerDocument.documentElement) return true;
  const gjsType = target.getAttribute && target.getAttribute('data-gjs-type');
  if (gjsType === 'wrapper') return true;
  return false;
}

/** Build a Map<HTMLElement, Component> by walking the wrapper tree. */
function buildElementToComponentMap(editor) {
  const map = new Map();
  const wrapper = editor.getWrapper && editor.getWrapper();
  if (!wrapper) return map;
  const visit = (comp) => {
    const el = typeof comp.getEl === 'function' ? comp.getEl() : null;
    if (el) map.set(el, comp);
    const children = typeof comp.components === 'function' ? comp.components() : null;
    if (children && typeof children.each === 'function') {
      children.each((child) => visit(child));
    }
  };
  visit(wrapper);
  return map;
}

/** Walk up from `el` to find the nearest element backed by a GrapesJS component. */
function findComponentForElement(editor, el) {
  if (!el) return null;
  const map = buildElementToComponentMap(editor);
  let cur = el;
  while (cur && cur.nodeType === 1) {
    if (map.has(cur)) {
      const comp = map.get(cur);
      const wrapper = editor.getWrapper && editor.getWrapper();
      if (comp === wrapper) return null;
      return comp;
    }
    cur = cur.parentNode;
  }
  return null;
}

function rectsIntersect(a, b) {
  return !(b.left >= a.right || b.right <= a.left || b.top >= a.bottom || b.bottom <= a.top);
}

/** Is `ancestor` an ancestor of `comp` in the component tree? */
function isAncestorComponent(ancestor, comp) {
  let cur = comp && comp.parent ? comp.parent() : null;
  while (cur) {
    if (cur === ancestor) return true;
    cur = cur.parent ? cur.parent() : null;
  }
  return false;
}

/** Collect components whose DOM rect intersects the marquee, deduped to topmost matches. */
function findComponentsInRect(editor, pageRect, win, doc) {
  const wrapper = editor.getWrapper && editor.getWrapper();
  if (!wrapper) return [];

  const scrollX = win.scrollX || doc.documentElement.scrollLeft || 0;
  const scrollY = win.scrollY || doc.documentElement.scrollTop || 0;

  const matches = [];
  const visit = (comp) => {
    if (comp !== wrapper) {
      const el = typeof comp.getEl === 'function' ? comp.getEl() : null;
      if (el && el.getBoundingClientRect) {
        const r = el.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) {
          const cmpRect = {
            left: r.left + scrollX,
            top: r.top + scrollY,
            right: r.right + scrollX,
            bottom: r.bottom + scrollY,
          };
          if (rectsIntersect(pageRect, cmpRect)) {
            matches.push(comp);
            // Don't descend into matched components — keep selection at the
            // topmost intersecting level (matches Figma-style marquee).
            return;
          }
        }
      }
    }
    const children = typeof comp.components === 'function' ? comp.components() : null;
    if (children && typeof children.each === 'function') {
      children.each((child) => visit(child));
    }
  };
  visit(wrapper);

  // Defensive dedupe: drop any match that's a descendant of another match.
  return matches.filter(
    (m) => !matches.some((other) => other !== m && isAncestorComponent(other, m))
  );
}

function mergeUniqueComponents(...lists) {
  const seen = new Set();
  const out = [];
  for (const list of lists) {
    for (const c of list) {
      const key = c && (c.cid || (typeof c.getId === 'function' ? c.getId() : c));
      if (!key || seen.has(key)) continue;
      seen.add(key);
      out.push(c);
    }
  }
  return out;
}

/** Attach listeners to a single iframe instance; returns a teardown fn. */
function attachToFrame(editor) {
  const frame = editor.Canvas && editor.Canvas.getFrameEl && editor.Canvas.getFrameEl();
  if (!frame || !frame.contentDocument || !frame.contentWindow) {
    return () => {};
  }

  const doc = frame.contentDocument;
  const win = frame.contentWindow;
  const body = doc.body;
  if (!body) return () => {};

  injectMarqueeStyles(doc);

  let startPageX = 0;
  let startPageY = 0;
  let marqueeEl = null;
  let dragging = false;
  let additive = false;
  let baseSelection = [];

  const removeMarqueeEl = () => {
    if (marqueeEl && marqueeEl.parentNode) {
      marqueeEl.parentNode.removeChild(marqueeEl);
    }
    marqueeEl = null;
  };

  const cancelDrag = () => {
    dragging = false;
    additive = false;
    baseSelection = [];
    body.classList.remove(ACTIVE_BODY_CLASS);
    removeMarqueeEl();
  };

  const onMouseDown = (e) => {
    if (e.button !== 0) return;
    const isAdditive = e.shiftKey || e.metaKey || e.ctrlKey;

    if (!isEmptyCanvasTarget(e.target, body)) {
      // Shift/Ctrl/Meta + click on a component => toggle it in selection.
      if (isAdditive) {
        const comp = findComponentForElement(editor, e.target);
        if (comp) {
          const current = editor.getSelectedAll ? editor.getSelectedAll() : [];
          const alreadySelected = current.some(
            (c) => c === comp || (c && comp && c.cid && c.cid === comp.cid)
          );
          if (alreadySelected) {
            if (typeof editor.selectRemove === 'function') {
              editor.selectRemove(comp);
            } else {
              const next = current.filter((c) => c !== comp);
              editor.select(next.length ? next : undefined);
            }
          } else if (typeof editor.selectAdd === 'function') {
            editor.selectAdd(comp);
          } else {
            editor.select(mergeUniqueComponents(current, [comp]));
          }
          e.preventDefault();
          e.stopPropagation();
        }
      }
      return;
    }

    additive = isAdditive;
    baseSelection = additive && editor.getSelectedAll ? editor.getSelectedAll().slice() : [];
    startPageX = e.pageX;
    startPageY = e.pageY;
    dragging = true;
    body.classList.add(ACTIVE_BODY_CLASS);

    marqueeEl = doc.createElement('div');
    marqueeEl.className = MARQUEE_CLASS;
    marqueeEl.style.left = `${startPageX}px`;
    marqueeEl.style.top = `${startPageY}px`;
    marqueeEl.style.width = '0px';
    marqueeEl.style.height = '0px';
    body.appendChild(marqueeEl);

    e.preventDefault();
  };

  const onMouseMove = (e) => {
    if (!dragging || !marqueeEl) return;
    const x = e.pageX;
    const y = e.pageY;
    const left = Math.min(startPageX, x);
    const top = Math.min(startPageY, y);
    marqueeEl.style.left = `${left}px`;
    marqueeEl.style.top = `${top}px`;
    marqueeEl.style.width = `${Math.abs(x - startPageX)}px`;
    marqueeEl.style.height = `${Math.abs(y - startPageY)}px`;
  };

  const onMouseUp = (e) => {
    if (!dragging) return;
    const endPageX = typeof e.pageX === 'number' ? e.pageX : startPageX;
    const endPageY = typeof e.pageY === 'number' ? e.pageY : startPageY;
    const left = Math.min(startPageX, endPageX);
    const top = Math.min(startPageY, endPageY);
    const right = Math.max(startPageX, endPageX);
    const bottom = Math.max(startPageY, endPageY);
    const width = right - left;
    const height = bottom - top;

    const wasAdditive = additive;
    const prevSelection = baseSelection;
    cancelDrag();

    if (width < MIN_DRAG_PX && height < MIN_DRAG_PX) {
      // Treat as a normal click on empty canvas: deselect everything
      // (unless additive, in which case preserve the prior selection).
      if (!wasAdditive) {
        editor.select(undefined);
      }
      return;
    }

    const hits = findComponentsInRect(editor, { left, top, right, bottom }, win, doc);
    const next = wasAdditive ? mergeUniqueComponents(prevSelection, hits) : hits;
    editor.select(next.length ? next : undefined);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape' && dragging) {
      cancelDrag();
    }
  };

  doc.addEventListener('mousedown', onMouseDown, true);
  doc.addEventListener('mousemove', onMouseMove, true);
  doc.addEventListener('mouseup', onMouseUp, true);
  doc.addEventListener('keydown', onKeyDown, true);

  // Cover the case where the cursor leaves the iframe before mouseup.
  const parentDoc =
    win.parent && win.parent !== win && win.parent.document ? win.parent.document : null;
  if (parentDoc) {
    parentDoc.addEventListener('mouseup', onMouseUp, true);
  }

  return () => {
    doc.removeEventListener('mousedown', onMouseDown, true);
    doc.removeEventListener('mousemove', onMouseMove, true);
    doc.removeEventListener('mouseup', onMouseUp, true);
    doc.removeEventListener('keydown', onKeyDown, true);
    if (parentDoc) {
      parentDoc.removeEventListener('mouseup', onMouseUp, true);
    }
    cancelDrag();
  };
}

/**
 * Install marquee + shift-click multi-select on the editor's canvas. Re-attaches
 * automatically whenever the canvas iframe reloads (e.g. on device change).
 */
export function setupMarqueeSelection(editor) {
  let tearDownFrame = () => {};
  const reattach = () => {
    tearDownFrame();
    tearDownFrame = attachToFrame(editor);
  };

  editor.on('canvas:frame:load', reattach);
  // Attach immediately if the frame is already ready (init path).
  reattach();

  return () => {
    editor.off('canvas:frame:load', reattach);
    tearDownFrame();
  };
}
